import { Box } from '@mui/material'
import { createChart, CrosshairMode, IChartApi, ISeriesApi } from 'lightweight-charts'
import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

import useResizeObserver from '@/hooks/use-resize-observer'
import {
  resizeChart,
  toHistogramData,
  useLiquidityChart,
} from '@/providers/liquidity-chart'
import { TokenPair } from '@/providers/liquidity-chart/candle-utils'
import { getTickers, tickerTimeFrameToSeconds, TradingViewTickerTimeFrame } from '@/utils/apis/tickers'

import { PriceChartProps } from './types'
import usePriceSocket from './use-price-socket'
import { getQueryKeyTickers, getQueryOptionsTickers,mergeOrStartNewCandle } from './utils'

export type Simple24hLineChartProps = PriceChartProps & {
  options?: {
    candleWindowSize?: number
    timeFrame?: TradingViewTickerTimeFrame
  }
}

const Simple24hLineChart = ({
  options = {},
  tokenA,
  tokenB,
  onCandleListUpdated,
  onPriceSentFromSocket,
}: Simple24hLineChartProps) => {
  const { candleWindowSize = 24, timeFrame = 'one_hour' } = options
  const span = tickerTimeFrameToSeconds[timeFrame]

  const queryClient = useQueryClient()
  const [element, setElement] = useState<HTMLElement>()
  const [chart, setChart] = useState<IChartApi>()
  const [series, setSeries] = useState<ISeriesApi<'Line'>>()
  const { lastMessage } = usePriceSocket(tokenA, tokenB)

  const tokenPair = TokenPair.of(tokenA.symbol, tokenB.symbol)
  const { candles, updateAll, updateLast } = useLiquidityChart(null, { span, tokenPair })

  useEffect(() => {
    onCandleListUpdated?.(candles)
    series?.setData(toHistogramData(candles.slice(-candleWindowSize), 'close'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, candleWindowSize])

  useEffect(() => {
    if (!element) return

    const chart = createChart(element, {
      crosshair: {
        mode: CrosshairMode.Hidden,
      },
      grid: {
        horzLines: {
          visible: false,
        },
        vertLines: {
          visible: false,
        },
      },
      layout: {
        background: {
          color: 'transparent',
        },
      },
      leftPriceScale: {
        visible: false,
      },
      rightPriceScale: {
        visible: false,
      },
      timeScale: {
        fixLeftEdge: true,
        fixRightEdge: true,
        visible: false,
      },
    })
    setChart(chart)

    const series = chart.addLineSeries({
      baseLineWidth: 1,
      color: '#FFFFFF',
      lineWidth: 1,
      priceLineVisible: false,
    })
    setSeries(series)

    series.subscribeDataChanged(() => {
      chart.timeScale().fitContent()
    })

    resizeChart(chart)

    let chartRemoved = false
    queryClient
      .fetchQuery(
        getQueryKeyTickers(tokenA, tokenB, span),
        () => getTickers(tokenA, tokenB, timeFrame),
        getQueryOptionsTickers(span),
      )
      .then((tickerData) => {
        updateAll(tickerData.candles)

        if (!chartRemoved) {
          series.setData(toHistogramData(tickerData.candles.slice(-candleWindowSize), 'close'))
        }
      })

    return () => {
      chartRemoved = true

      chart.remove()
      setChart(undefined)
      setSeries(undefined)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candleWindowSize, element, span, timeFrame, tokenA, tokenB])

  useEffect(() => {
    if (!lastMessage) return

    onPriceSentFromSocket?.(lastMessage.data)

    const mergedCandle = mergeOrStartNewCandle(
      candles[candles.length - 1],
      lastMessage.data,
      span,
    )
    updateLast(mergedCandle)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage])

  useEffect(() => {
    const firstCandle = candles[candles.length - candleWindowSize]
    const lastCandle = candles[candles.length - 1]
    if (firstCandle && lastCandle && firstCandle.open < lastCandle.close) {
      series?.applyOptions({ color: '#55B964' })
    } else if (firstCandle && lastCandle && firstCandle.open > lastCandle.close) {
      series?.applyOptions({ color: '#FF6701' })
    } else {
      series?.applyOptions({ color: '#FFFFFF' })
    }
  }, [candles, candleWindowSize, series])

  useResizeObserver({
    ref: element,
    onResize: () => {
      if (chart) {
        resizeChart(chart)
        chart.timeScale().fitContent()
      }
    },
  })

  return (
    <Box className="w-full h-full relative">
      <Box className="absolute inset-0 pointer-events-none" ref={setElement} />
    </Box>
  )
}

export default Simple24hLineChart
